import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageShell } from '@components/common/PageShell'
import { ResponsiveTabs, type ResponsiveTabItem } from '@components/common/ResponsiveTabs'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import { JobHistoryTab } from '../components/JobHistoryTab'
import { PopularTimesTab } from '../components/PopularTimesTab'
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  TextField,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import {
  Stop,
  Refresh,
  TrendingUp,
  People,
  Explore,
  Groups,
  Schedule,
  FilterList,
  Settings,
  DateRange,
  History,
  AccessTime,
} from '@mui/icons-material'
import { statisticsApi } from '@api/endpoints/statistics'
import { useCategoryLookup } from '@features/admin/hooks/useAdventureCategories'
import {
  StatisticsJobStatusDto,
  DailyUserStatisticsDto,
  DailyAdventureStatisticsDto,
  DailyGroupStatisticsDto,
  TopGroupDto,
  UserStatisticsSortBy,
  AdventureStatisticsSortBy,
} from '@api/types/statistics'

/** Tabs by key (`?tab=`): Job history and Popular times are linkable. Popular times = Backlog 062. */
const TABS: ResponsiveTabItem[] = [
  { key: 'users', label: 'Users', icon: <People /> },
  { key: 'adventures', label: 'Adventures', icon: <Explore /> },
  { key: 'groups', label: 'Groups', icon: <Groups /> },
  { key: 'history', label: 'Job History', icon: <History /> },
  { key: 'popular-times', label: 'Popular times', icon: <AccessTime /> },
]

/** The date-range tables; the other tabs bring their own data. */
const TABLE_TABS = new Set(['users', 'adventures', 'groups'])

const formatDate = (dateStr?: string) => {
  if (!dateStr) return 'N/A'
  const date = new Date(dateStr)
  return isNaN(date.getTime()) ? 'N/A' : date.toLocaleString()
}

const formatShortDate = (dateStr: string) => {
  // A bare yyyy-MM-dd parses as UTC midnight, which is the previous day west of Greenwich.
  const date = new Date(dateStr.length === 10 ? `${dateStr}T00:00:00` : dateStr)
  return isNaN(date.getTime()) ? dateStr : date.toLocaleDateString()
}

const errorText = (err: unknown, fallback: string) => (err instanceof Error ? err.message : fallback)

/** Up to two "Group (n)" chips for a top-groups list; they wrap inside their cell. */
function TopGroups({ groups }: { groups?: TopGroupDto[] | null }) {
  if (!groups?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    )
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {groups.slice(0, 2).map((g) => (
        <Chip key={g.groupId} label={`${g.groupName} (${g.count})`} size="small" sx={{ maxWidth: '100%' }} />
      ))}
    </Box>
  )
}

function SummaryCard({ icon, title, value, caption, captionColor = 'text.secondary' }: {
  icon: React.ReactNode
  title: string
  value: React.ReactNode
  caption: string
  captionColor?: string
}) {
  return (
    <Card>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {icon}
          <Typography variant="subtitle2" color="text.secondary">
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.6rem', sm: '2.125rem' } }}>
          {value}
        </Typography>
        <Typography variant="body2" color={captionColor}>
          {caption}
        </Typography>
      </CardContent>
    </Card>
  )
}

export default function StatisticsPage() {
  const dialogProps = useMobileDialogProps()
  const [searchParams, setSearchParams] = useSearchParams()

  // Job status state
  const [jobStatus, setJobStatus] = useState<StatisticsJobStatusDto | null>(null)
  const [jobLoading, setJobLoading] = useState(true)
  const [jobError, setJobError] = useState<string | null>(null)
  const [stopping, setStopping] = useState(false)
  const [confirmStop, setConfirmStop] = useState(false)

  // Statistics date range (applied) and the dialog's draft of it
  const [statsStartDate, setStatsStartDate] = useState(() => {
    const date = new Date()
    date.setDate(date.getDate() - 30)
    return date.toISOString().split('T')[0]
  })
  const [statsEndDate, setStatsEndDate] = useState(() => new Date().toISOString().split('T')[0])
  const [draftStart, setDraftStart] = useState('')
  const [draftEnd, setDraftEnd] = useState('')

  // Statistics data. Each table has its own loading/error: they used to share one flag, so
  // whichever request finished first cleared the spinner for the other two, and a failed groups
  // request only reached the console and showed "No data available".
  const [userStats, setUserStats] = useState<DailyUserStatisticsDto[]>([])
  const [adventureStats, setAdventureStats] = useState<DailyAdventureStatisticsDto[]>([])
  const [groupStats, setGroupStats] = useState<DailyGroupStatisticsDto[]>([])
  const [userLoading, setUserLoading] = useState(false)
  const [adventureLoading, setAdventureLoading] = useState(false)
  const [groupLoading, setGroupLoading] = useState(false)
  const [userError, setUserError] = useState<string | null>(null)
  const [adventureError, setAdventureError] = useState<string | null>(null)
  const [groupError, setGroupError] = useState<string | null>(null)

  // User stats pagination and sorting
  const [userPage, setUserPage] = useState(0)
  const [userPageSize, setUserPageSize] = useState(25)
  const [userTotalCount, setUserTotalCount] = useState(0)
  const [userSortBy, setUserSortBy] = useState<UserStatisticsSortBy>('Date')
  const [userSortDesc, setUserSortDesc] = useState(true)

  // Adventure stats pagination and sorting
  const [adventurePage, setAdventurePage] = useState(0)
  const [adventurePageSize, setAdventurePageSize] = useState(25)
  const [adventureTotalCount, setAdventureTotalCount] = useState(0)
  const [adventureSortBy, setAdventureSortBy] = useState<AdventureStatisticsSortBy>('Date')
  const [adventureSortDesc, setAdventureSortDesc] = useState(true)

  // Group stats pagination and sorting
  const [groupPage, setGroupPage] = useState(0)
  const [groupPageSize, setGroupPageSize] = useState(25)
  const [groupTotalCount, setGroupTotalCount] = useState(0)
  const [groupSortDesc, setGroupSortDesc] = useState(true)

  // Tab state
  const [activeTab, setActiveTab] = useState('users')
  // Popular times settings hold drafts that die when the tab unmounts; leaving with edits asks first.
  const [settingsDirty, setSettingsDirty] = useState(false)
  const [pendingTab, setPendingTab] = useState<string | null>(null)

  // Adventure category filter
  const [categoryFilter, setCategoryFilter] = useState<number | undefined>(undefined)
  // Filter options and row labels come from the category rows
  const { active: activeCategories, byId: categoryById } = useCategoryLookup()
  const categoryOptions: { value: number | undefined; label: string }[] = [
    { value: undefined, label: 'All Categories' },
    ...activeCategories.map((c) => ({ value: c.id, label: c.displayName })),
  ]
  const getCategoryName = (categoryId: number) =>
    categoryById.get(categoryId)?.displayName || `Category ${categoryId}`
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null)

  // Dialog state
  const [dateRangeDialogOpen, setDateRangeDialogOpen] = useState(false)
  const [jobDialogOpen, setJobDialogOpen] = useState(false)

  // Fetch job status
  const fetchJobStatus = useCallback(async () => {
    try {
      const status = await statisticsApi.getStatus()
      setJobStatus(status)
      setJobError(null)
    } catch (err) {
      setJobError(errorText(err, 'Failed to fetch job status'))
    } finally {
      setJobLoading(false)
    }
  }, [])

  const fetchUserStatistics = useCallback(async () => {
    setUserLoading(true)
    setUserError(null)
    try {
      const response = await statisticsApi.getUserStatistics({
        startDate: statsStartDate || undefined,
        endDate: statsEndDate || undefined,
        page: userPage + 1,
        pageSize: userPageSize,
        sortBy: userSortBy,
        sortDescending: userSortDesc,
      })
      setUserStats(response.items || [])
      setUserTotalCount(response.totalCount || 0)
    } catch (err) {
      setUserError(errorText(err, 'Failed to fetch user statistics'))
    } finally {
      setUserLoading(false)
    }
  }, [statsStartDate, statsEndDate, userPage, userPageSize, userSortBy, userSortDesc])

  const fetchAdventureStatistics = useCallback(async () => {
    setAdventureLoading(true)
    setAdventureError(null)
    try {
      const response = await statisticsApi.getAdventureStatistics({
        startDate: statsStartDate || undefined,
        endDate: statsEndDate || undefined,
        category: categoryFilter,
        page: adventurePage + 1,
        pageSize: adventurePageSize,
        sortBy: adventureSortBy,
        sortDescending: adventureSortDesc,
      })
      setAdventureStats(response.items || [])
      setAdventureTotalCount(response.totalCount || 0)
    } catch (err) {
      setAdventureError(errorText(err, 'Failed to fetch adventure statistics'))
    } finally {
      setAdventureLoading(false)
    }
  }, [statsStartDate, statsEndDate, categoryFilter, adventurePage, adventurePageSize, adventureSortBy, adventureSortDesc])

  const fetchGroupStatistics = useCallback(async () => {
    setGroupLoading(true)
    setGroupError(null)
    try {
      const response = await statisticsApi.getGroupStatistics({
        startDate: statsStartDate || undefined,
        endDate: statsEndDate || undefined,
        page: groupPage + 1,
        pageSize: groupPageSize,
        sortDescending: groupSortDesc,
      })
      setGroupStats(response.items || [])
      setGroupTotalCount(response.totalCount || 0)
    } catch (err) {
      setGroupError(errorText(err, 'Failed to fetch group statistics'))
    } finally {
      setGroupLoading(false)
    }
  }, [statsStartDate, statsEndDate, groupPage, groupPageSize, groupSortDesc])

  // Stop rebuild (confirmed first: it abandons a running job part-way)
  const handleStopRebuild = async () => {
    setStopping(true)
    setJobError(null)
    try {
      await statisticsApi.stopRebuild()
      await fetchJobStatus()
    } catch (err) {
      setJobError(errorText(err, 'Failed to stop rebuild'))
    } finally {
      setStopping(false)
      setConfirmStop(false)
    }
  }

  // Initial job status load (only once)
  useEffect(() => {
    fetchJobStatus()
  }, [fetchJobStatus])

  // Each table refetches when its own pagination/sorting/filter or the date range changes.
  useEffect(() => {
    fetchUserStatistics()
  }, [fetchUserStatistics])

  useEffect(() => {
    fetchAdventureStatistics()
  }, [fetchAdventureStatistics])

  useEffect(() => {
    fetchGroupStatistics()
  }, [fetchGroupStatistics])

  // Auto-refresh job status when running
  useEffect(() => {
    if (jobStatus?.isRunning) {
      const interval = setInterval(fetchJobStatus, 5000)
      return () => clearInterval(interval)
    }
  }, [jobStatus?.isRunning, fetchJobStatus])

  const writeTabParam = (key: string) => {
    // ResponsiveTabs follows ?tab=, so a programmatic switch (or an undone one) has to write the
    // URL too, or its effect snaps the tab back to whatever the URL still says.
    const next = new URLSearchParams(searchParams)
    next.set('tab', key)
    setSearchParams(next, { replace: true })
  }

  const changeTab = (key: string) => {
    if (activeTab === 'popular-times' && key !== 'popular-times' && settingsDirty) {
      setPendingTab(key)
      return
    }
    setActiveTab(key)
  }

  const refreshAll = () => {
    fetchJobStatus()
    fetchUserStatistics()
    fetchAdventureStatistics()
    fetchGroupStatistics()
  }

  const handleUserSortChange = (column: string) => {
    if (userSortBy === column) {
      setUserSortDesc(!userSortDesc)
    } else {
      setUserSortBy(column as UserStatisticsSortBy)
      setUserSortDesc(true)
    }
    setUserPage(0)
  }

  const handleAdventureSortChange = (column: string) => {
    if (adventureSortBy === column) {
      setAdventureSortDesc(!adventureSortDesc)
    } else {
      setAdventureSortBy(column as AdventureStatisticsSortBy)
      setAdventureSortDesc(true)
    }
    setAdventurePage(0)
  }

  const setCategory = (value: number | undefined) => {
    setCategoryFilter(value)
    setAdventurePage(0)
  }

  const openDateRange = () => {
    setDraftStart(statsStartDate)
    setDraftEnd(statsEndDate)
    setDateRangeDialogOpen(true)
  }

  const draftInvalid = Boolean(draftStart && draftEnd && draftStart > draftEnd)

  const applyDateRange = () => {
    setUserPage(0)
    setAdventurePage(0)
    setGroupPage(0)
    // Setting the dates refetches all three tables through their effects.
    setStatsStartDate(draftStart)
    setStatsEndDate(draftEnd)
    setDateRangeDialogOpen(false)
  }

  // Calculate summary stats
  const latestUserStats = userStats[userStats.length - 1]
  const totalNewUsers = userStats.reduce((sum, s) => sum + s.newUsersCount, 0)
  const totalNewAdventures = adventureStats.reduce((sum, s) => sum + s.newAdventuresCount, 0)
  const totalSoloCompleted = adventureStats.reduce((sum, s) => sum + s.soloSessionsCompleted, 0)
  const totalGroupCompleted = adventureStats.reduce((sum, s) => sum + s.groupSessionsCompleted, 0)
  const totalCompletions = totalSoloCompleted + totalGroupCompleted
  const daysWithGroupActivity = groupStats.filter(
    (s) => s.topGroupsByActiveMembers?.length || s.topGroupsByPosts?.length,
  ).length

  const rangeLabel = `${statsStartDate ? formatShortDate(statsStartDate) : 'Start'} – ${
    statsEndDate ? formatShortDate(statsEndDate) : 'Today'
  }`

  const userColumns: DataTableColumn<DailyUserStatisticsDto>[] = [
    { key: 'date', header: 'Date', sortKey: 'Date', primary: true, render: (s) => formatShortDate(s.statisticsDate) },
    { key: 'new', header: 'New', sortKey: 'NewUsers', align: 'right', render: (s) => s.newUsersCount },
    { key: 'active', header: 'Active', sortKey: 'ActiveUsers', align: 'right', render: (s) => s.activeUsersCount },
    {
      key: 'subs',
      header: 'Subscriptions',
      sortKey: 'Subscriptions',
      align: 'right',
      render: (s) => s.activeSubscriptionsCount,
    },
    { key: 'total', header: 'Total', sortKey: 'Total', align: 'right', render: (s) => s.totalUsersCount },
  ]

  const adventureColumns: DataTableColumn<DailyAdventureStatisticsDto>[] = [
    { key: 'date', header: 'Date', sortKey: 'Date', primary: true, render: (s) => formatShortDate(s.statisticsDate) },
    // With a category picked every row is that category, so the column says nothing.
    ...(categoryFilter === undefined
      ? [
          {
            key: 'category',
            header: 'Category',
            sortKey: 'Category',
            render: (s: DailyAdventureStatisticsDto) => (
              <Chip label={getCategoryName(s.mainCategory)} size="small" variant="outlined" />
            ),
          },
        ]
      : []),
    { key: 'new', header: 'New', sortKey: 'NewAdventures', align: 'right', render: (s) => s.newAdventuresCount },
    {
      key: 'soloStarted',
      header: 'Solo Started',
      sortKey: 'SoloStarted',
      align: 'right',
      hideBelow: 'md',
      render: (s) => s.soloSessionsStarted,
    },
    {
      key: 'soloCompleted',
      header: 'Solo Completed',
      sortKey: 'SoloCompleted',
      align: 'right',
      render: (s) => s.soloSessionsCompleted,
    },
    {
      key: 'groupStarted',
      header: 'Group Started',
      sortKey: 'GroupStarted',
      align: 'right',
      hideBelow: 'md',
      render: (s) => s.groupSessionsStarted,
    },
    {
      key: 'groupCompleted',
      header: 'Group Completed',
      sortKey: 'GroupCompleted',
      align: 'right',
      render: (s) => s.groupSessionsCompleted,
    },
  ]

  const groupColumns: DataTableColumn<DailyGroupStatisticsDto>[] = [
    { key: 'date', header: 'Date', sortKey: 'Date', primary: true, render: (s) => formatShortDate(s.statisticsDate) },
    { key: 'members', header: 'Top by Active Members', render: (s) => <TopGroups groups={s.topGroupsByActiveMembers} /> },
    { key: 'posts', header: 'Top by Posts', render: (s) => <TopGroups groups={s.topGroupsByPosts} /> },
    { key: 'events', header: 'Top by Events', hideBelow: 'md', render: (s) => <TopGroups groups={s.topGroupsByEvents} /> },
    {
      key: 'adventures',
      header: 'Top by Adventures',
      hideBelow: 'md',
      render: (s) => <TopGroups groups={s.topGroupsByAdventures} />,
    },
  ]

  const isTableTab = TABLE_TABS.has(activeTab)

  return (
    <PageShell
      title="Statistics"
      subtitle={isTableTab ? `Showing ${rangeLabel}` : undefined}
      actions={
        <>
          <Button variant="outlined" startIcon={<Refresh />} onClick={refreshAll}>
            Refresh
          </Button>
          <Button
            variant="outlined"
            color={jobStatus?.isRunning ? 'success' : 'primary'}
            startIcon={jobStatus?.isRunning ? <CircularProgress size={16} color="inherit" /> : <Settings />}
            onClick={() => setJobDialogOpen(true)}
          >
            {jobStatus?.isRunning ? 'Rebuild running' : 'Rebuild status'}
          </Button>
        </>
      }
    >
      <Box sx={{ width: '100%', minWidth: 0 }}>
        {jobError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setJobError(null)}>
            {jobError}
          </Alert>
        )}

        <ResponsiveTabs tabs={TABS} value={activeTab} onChange={changeTab} sx={{ mb: 2 }} />

        {isTableTab && (
          <>
            {/* The range and filter are always visible as text; they used to live only in tooltips. */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
              <Button variant="outlined" size="small" startIcon={<DateRange />} onClick={openDateRange} sx={{ minHeight: 36 }}>
                {rangeLabel}
              </Button>
              {activeTab === 'adventures' &&
                (categoryFilter === undefined ? (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FilterList />}
                    onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                    sx={{ minHeight: 36 }}
                  >
                    All categories
                  </Button>
                ) : (
                  <Chip
                    color="primary"
                    icon={<FilterList />}
                    label={`Category: ${getCategoryName(categoryFilter)}`}
                    onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                    onDelete={() => setCategory(undefined)}
                  />
                ))}
              <Menu anchorEl={filterAnchorEl} open={Boolean(filterAnchorEl)} onClose={() => setFilterAnchorEl(null)}>
                {categoryOptions.map((opt) => (
                  <MenuItem
                    key={opt.value ?? 'all'}
                    selected={categoryFilter === opt.value}
                    onClick={() => {
                      setCategory(opt.value)
                      setFilterAnchorEl(null)
                    }}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </Menu>
            </Box>

            {/* Summary cards: two across on a phone, auto-fit above. */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: { xs: 1, sm: 2 },
                mb: { xs: 2, md: 3 },
                '& > *': { flex: { xs: '1 1 140px', sm: '1 1 200px' }, minWidth: 0, maxWidth: '100%' },
              }}
            >
              <SummaryCard
                icon={<People color="primary" />}
                title="Total Users"
                value={latestUserStats?.totalUsersCount ?? '-'}
                caption={`+${totalNewUsers} new in period`}
                captionColor="success.main"
              />
              <SummaryCard
                icon={<Explore color="primary" />}
                title="New Adventures"
                value={totalNewAdventures}
                caption="in selected period"
              />
              <SummaryCard
                icon={<TrendingUp color="primary" />}
                title="Sessions Completed"
                value={totalCompletions}
                caption={`${totalSoloCompleted} solo, ${totalGroupCompleted} group`}
              />
              <SummaryCard
                icon={<Groups color="primary" />}
                title="Group Activity"
                value={daysWithGroupActivity}
                caption="days with activity"
              />
            </Box>
          </>
        )}

        {activeTab === 'users' && (
          <DataTable
            rows={userStats}
            columns={userColumns}
            getRowKey={(s) => s.statisticsDate}
            loading={userLoading}
            error={userError}
            onRetry={fetchUserStatistics}
            emptyMessage="No user statistics in this range."
            page={userPage}
            pageSize={userPageSize}
            total={userTotalCount}
            onPageChange={setUserPage}
            onPageSizeChange={setUserPageSize}
            sortBy={userSortBy}
            sortDescending={userSortDesc}
            onSortChange={handleUserSortChange}
            pagerPosition="both"
            renderCard={(s) => (
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {formatShortDate(s.statisticsDate)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  New {s.newUsersCount} · Active {s.activeUsersCount} · Subs {s.activeSubscriptionsCount} · Total{' '}
                  {s.totalUsersCount.toLocaleString()}
                </Typography>
              </Box>
            )}
          />
        )}

        {activeTab === 'adventures' && (
          <DataTable
            rows={adventureStats}
            columns={adventureColumns}
            getRowKey={(s) => `${s.statisticsDate}-${s.mainCategory}`}
            loading={adventureLoading}
            error={adventureError}
            onRetry={fetchAdventureStatistics}
            emptyMessage="No adventure statistics in this range."
            page={adventurePage}
            pageSize={adventurePageSize}
            total={adventureTotalCount}
            onPageChange={setAdventurePage}
            onPageSizeChange={setAdventurePageSize}
            sortBy={adventureSortBy}
            sortDescending={adventureSortDesc}
            onSortChange={handleAdventureSortChange}
            pagerPosition="both"
            renderCard={(s) => (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {formatShortDate(s.statisticsDate)}
                  </Typography>
                  {categoryFilter === undefined && (
                    <Chip label={getCategoryName(s.mainCategory)} size="small" variant="outlined" />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  New {s.newAdventuresCount} · Solo {s.soloSessionsCompleted}/{s.soloSessionsStarted} · Group{' '}
                  {s.groupSessionsCompleted}/{s.groupSessionsStarted} completed/started
                </Typography>
              </Box>
            )}
          />
        )}

        {activeTab === 'groups' && (
          <DataTable
            rows={groupStats}
            columns={groupColumns}
            getRowKey={(s) => s.statisticsDate}
            loading={groupLoading}
            error={groupError}
            onRetry={fetchGroupStatistics}
            emptyMessage="No group statistics in this range."
            page={groupPage}
            pageSize={groupPageSize}
            total={groupTotalCount}
            onPageChange={setGroupPage}
            onPageSizeChange={setGroupPageSize}
            sortBy="Date"
            sortDescending={groupSortDesc}
            onSortChange={() => {
              setGroupSortDesc(!groupSortDesc)
              setGroupPage(0)
            }}
            pagerPosition="both"
          />
        )}

        {/* Job History Tab: Run now + history (launch, lock, record, poll) */}
        {activeTab === 'history' && <JobHistoryTab />}

        {/* Popular times (Backlog 062): status, recompute, synthetic data, settings */}
        {activeTab === 'popular-times' && <PopularTimesTab onSettingsDirtyChange={setSettingsDirty} />}

        {/* Date Range Dialog: edits a draft, so the tables do not refetch on every date-picker change. */}
        <Dialog
          open={dateRangeDialogOpen}
          onClose={() => setDateRangeDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          {...dialogProps}
        >
          <DialogTitle>View Date Range</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                type="date"
                label="From"
                value={draftStart}
                onChange={(e) => setDraftStart(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="date"
                label="To"
                value={draftEnd}
                onChange={(e) => setDraftEnd(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={draftInvalid}
                helperText={draftInvalid ? 'The end date must be on or after the start date.' : undefined}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDateRangeDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={applyDateRange} disabled={draftInvalid}>
              Apply
            </Button>
          </DialogActions>
        </Dialog>

        {/* Rebuild status. Starting a rebuild lives in one place, Job History > Run now (with its
            confirm and lock checks); this dialog used to have its own unconfirmed Start. */}
        <Dialog open={jobDialogOpen} onClose={() => setJobDialogOpen(false)} maxWidth="sm" fullWidth {...dialogProps}>
          <DialogTitle>Statistics Rebuild Job</DialogTitle>
          <DialogContent>
            {jobLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress />
              </Box>
            ) : jobStatus ? (
              <Box sx={{ pt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  <Chip
                    label={jobStatus.isRunning ? 'Running' : 'Idle'}
                    color={jobStatus.isRunning ? 'success' : 'default'}
                    icon={jobStatus.isRunning ? <Schedule /> : undefined}
                  />
                  {jobStatus.isRunning && jobStatus.currentJob && (
                    <Typography variant="body2" color="text.secondary">
                      {jobStatus.currentJob.recordsProcessed} records processed
                    </Typography>
                  )}
                </Box>

                {jobStatus.isRunning && jobStatus.currentJob && (
                  <Box sx={{ mb: 2, p: { xs: 1.5, sm: 2 }, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Current Job
                    </Typography>
                    <Typography variant="body2">Started: {formatDate(jobStatus.currentJob.startedAt)}</Typography>
                    {jobStatus.currentJob.dateRangeStart && (
                      <Typography variant="body2">
                        Date Range: {jobStatus.currentJob.dateRangeStart} to {jobStatus.currentJob.dateRangeEnd}
                      </Typography>
                    )}
                    <LinearProgress sx={{ mt: 1 }} />
                  </Box>
                )}

                {jobStatus.lastCompletedJob && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Last Completed Job
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Completed: {formatDate(jobStatus.lastCompletedJob.completedAt)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Duration: {jobStatus.lastCompletedJob.duration || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Records: {jobStatus.lastCompletedJob.recordsProcessed.toLocaleString()}
                    </Typography>
                    {jobStatus.lastCompletedJob.errorMessage && (
                      <Alert severity="error" sx={{ mt: 1, overflowWrap: 'anywhere' }}>
                        {jobStatus.lastCompletedJob.errorMessage}
                      </Alert>
                    )}
                  </Box>
                )}

                {jobStatus.dataSummary && (
                  <Box sx={{ mb: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Data Summary
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Range: {jobStatus.dataSummary.earliestDate} to {jobStatus.dataSummary.latestDate}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Users: {jobStatus.dataSummary.totalUserStatisticsDays} days | Adventures:{' '}
                      {jobStatus.dataSummary.totalAdventureStatisticsDays} days | Groups:{' '}
                      {jobStatus.dataSummary.totalGroupStatisticsDays} days
                    </Typography>
                  </Box>
                )}

                <Typography variant="body2" color="text.secondary">
                  To start a rebuild, use Job History &gt; Run now. It confirms the date range first and refuses to
                  start while another statistics job holds the lock.
                </Typography>
              </Box>
            ) : (
              <Alert severity="error">Could not load the rebuild status.</Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Button onClick={() => setJobDialogOpen(false)}>Close</Button>
            {jobStatus?.isRunning ? (
              <Button variant="contained" color="error" startIcon={<Stop />} onClick={() => setConfirmStop(true)}>
                Stop rebuild
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<History />}
                onClick={() => {
                  setJobDialogOpen(false)
                  writeTabParam('history')
                  changeTab('history')
                }}
              >
                Open Job History
              </Button>
            )}
          </DialogActions>
        </Dialog>

        <ConfirmDialog
          open={pendingTab != null}
          title="Discard unsaved settings?"
          message="Your popular-times setting changes have not been saved. Leaving this tab throws them away."
          confirmLabel="Discard and leave"
          cancelLabel="Stay"
          destructive
          onConfirm={() => {
            if (pendingTab) setActiveTab(pendingTab)
            setPendingTab(null)
          }}
          onClose={() => {
            // The tab bar already wrote the new ?tab=; put it back so the tab effect does not follow it.
            writeTabParam('popular-times')
            setPendingTab(null)
          }}
        />

        <ConfirmDialog
          open={confirmStop}
          title="Stop the statistics rebuild?"
          message="The job stops part-way. Days it has not reached keep their old figures until the next rebuild."
          confirmLabel="Stop rebuild"
          destructive
          loading={stopping}
          onConfirm={handleStopRebuild}
          onClose={() => setConfirmStop(false)}
        />
      </Box>
    </PageShell>
  )
}
